import { z } from 'zod';
import { EXERCISE_CATEGORY, PLAN_PHASE } from './enums.js';
import { EXERCISE_LOAD_LIMITS, formatMinutes, isHalfStep, setsTotalMinutes } from './training-load.js';
/**
 * Trainingsplanung: Uebungen, Skizzen und Trainingseinheiten.
 *
 * - Uebungen kommen aus dem Standardkatalog (im Code, fuer alle Vereine) oder
 *   gehoeren dem Verein (eigene Uebung, Kopie, Linkuebung).
 * - Die Skizze ist kein Bild, sondern eine kleine JSON-Struktur und wird in der
 *   Oberflaeche als SVG gezeichnet.
 * - Eine Einheit ist eine geordnete Liste von Eintraegen in drei Phasen. Haengt
 *   sie an einem Training, gehoert sie diesem; ohne Training ist sie eine Vorlage.
 */
const trimmed = (max) => z.string().trim().max(max);
const requiredText = (max) => z.string().trim().min(1, 'Pflichtfeld').max(max);
/* ------------------------------------------------------------------ *
 * Skizze
 * ------------------------------------------------------------------ */
export const DIAGRAM_PITCH = ['FULL', 'HALF', 'AREA'];
export const DIAGRAM_PITCH_LABELS = {
    FULL: 'Ganzes Feld',
    HALF: 'Halbes Feld',
    AREA: 'Freie Fläche',
};
/**
 * SWITCH ist der Positionswechsel: wohin ein Spieler nach seiner Aktion weiterwechselt
 * (Nachlaufen zur naechsten Station, Rotation). Er bewegt wie ein Laufweg den Spieler am Pfeilanfang.
 * LOFT ist der hohe Ball (Flanke, Chip, Flugball): er bewegt wie ein Pass den Ball, fliegt aber ueber das Feld,
 * laesst sich biegen (`via`) und hat keine Knickpunkte (`points`).
 * Ein Pass kann zusaetzlich "Hinwechseln" (`follow`): der Passgeber laeuft seinem Pass nach (siehe Pfeilfeld `follow`).
 */
export const DIAGRAM_ARROW_KIND = ['PASS', 'RUN', 'DRIBBLE', 'SHOT', 'LOFT', 'SWITCH'];
/**
 * Pfeilarten, die einen Spieler bewegen und sich ueber mehrere Schritte erstrecken duerfen (`stepEnd`): Laufweg, Dribbling
 * und Positionswechsel. Pass, Schuss und hoher Ball bewegen den Ball und gehoeren immer zu einem Schritt.
 */
export const DIAGRAM_MULTI_STEP_KINDS = ['RUN', 'DRIBBLE', 'SWITCH'];
/** A bis D sind Mannschaften, N ist neutral (z. B. Jokerspieler). */
export const DIAGRAM_TEAM = ['A', 'B', 'N', 'C', 'D'];
/** Drehung einer Huerde in Grad: quer oder schraeg zur Laufrichtung. */
export const DIAGRAM_HURDLE_ROTATION = [0, 45, 90, 135];
export const DIAGRAM_GOAL_SIZE = ['MINI', 'YOUTH', 'FULL'];
/**
 * Symbolgroesse einer Skizze: Faktor auf Spieler, Ball, Huetchen, Stange, Ring, Huerde,
 * Tore, Beschriftung und (maessig) die Pfeile. Fehlt das Feld, gilt 1 (Normal).
 */
export const DIAGRAM_SYMBOL_SCALE = [0.6, 0.8, 1];
export const DIAGRAM_SYMBOL_SCALE_LABELS = {
    0.6: 'Klein',
    0.8: 'Mittel',
    1: 'Normal',
};
/** Faktor eines Elements mit `size: 'S'` auf die Symbolgroesse der Skizze. */
export const DIAGRAM_SMALL_FACTOR = 0.7;
/** Hoechstzahl Elemente je Skizze - haelt die Skizze lesbar und die Daten klein. */
export const DIAGRAM_MAX_ELEMENTS = 80;
/** Laenge der Beschriftung einer Masslinie (z. B. „15 m“). */
export const DIAGRAM_MEASURE_LABEL_MAX = 12;
/** Hoechste Schrittnummer eines Pfeils im Ablauf. */
export const DIAGRAM_MAX_STEP = 20;
/** Laenge eines Textes auf dem Feld (eine Zeile). */
export const DIAGRAM_TEXT_MAX = 40;
/** Hoechstzahl Knickpunkte (Zwischenpunkte) eines Pfeils. */
export const DIAGRAM_ARROW_MAX_POINTS = 8;
/** Form einer Zone: Rechteck (Standard), Kreis (Ellipse im Rechteck) oder Dreieck (Spitze oben, drehbar). */
export const DIAGRAM_ZONE_SHAPE = ['rect', 'circle', 'triangle'];
export const DIAGRAM_ZONE_SHAPE_LABELS = {
    rect: 'Rechteck',
    circle: 'Kreis',
    triangle: 'Dreieck',
};
/** Farbe einer Zone; ohne Angabe gilt Weiss (wie bei allen Zonen vor der Farbwahl). */
export const DIAGRAM_ZONE_COLOR = ['white', 'yellow', 'red', 'blue', 'orange'];
export const DIAGRAM_ZONE_COLOR_LABELS = {
    white: 'Weiß',
    yellow: 'Gelb',
    red: 'Rot',
    blue: 'Blau',
    orange: 'Orange',
};
/** Drehung des Dreiecks in Grad: Spitze oben (0), rechts (90), unten (180), links (270). */
export const DIAGRAM_ZONE_ROTATION = [0, 90, 180, 270];
/**
 * Hoehe des Spielfelds in Zeichnungseinheiten bei Breite 100 (Breite 68 m = 100):
 * ganzes Feld 105 m, halbes 52,5 m, freie Flaeche 5:4. Abstaende in der Skizze
 * werden in diesen Einheiten gemessen, damit x und y gleich zaehlen.
 */
export const DIAGRAM_PITCH_HEIGHT = {
    FULL: (105 * 100) / 68,
    HALF: (52.5 * 100) / 68,
    AREA: 80,
};
/**
 * Feinster Schritt einer Koordinate, die der Editor erzeugt: Vielfache von 0,5 (ganze Zahlen bleiben gueltig).
 * Das Schema prueft das bewusst nicht: aeltere Skizzen und Katalogubungen tragen Nachkommastellen (z. B. 33,3), und
 * eine strengere Pruefung machte jede gespeicherte Kopie davon beim naechsten Speichern ungueltig.
 */
export const DIAGRAM_COORDINATE_STEP = 0.5;
/** Rundet auf das Raster der Koordinaten (Vielfache von 0,5). */
export function roundCoordinate(value) {
    const rounded = Math.round(value / DIAGRAM_COORDINATE_STEP) * DIAGRAM_COORDINATE_STEP;
    // Aus -0 wird 0: sonst unterscheiden sich gleiche Orte in einem JSON-Vergleich.
    return rounded === 0 ? 0 : rounded;
}
/** Koordinaten liegen in Prozent des Spielfelds: 0 links/oben, 100 rechts/unten. */
const coordinate = z.number().min(0).max(100);
const point = z.tuple([coordinate, coordinate]);
/** Spieler, Ball und Ausstattung lassen sich einzeln kleiner zeichnen (Faktor `DIAGRAM_SMALL_FACTOR`). */
const smallSize = z.literal('S').optional();
export const diagramElementSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('player'),
        /** A bis D sind Mannschaften, N ist neutral (z. B. Jokerspieler). */
        team: z.enum(DIAGRAM_TEAM),
        x: coordinate,
        y: coordinate,
        label: z.string().trim().min(1).max(4).optional(),
        /**
         * Blickrichtung in Grad (ganze Zahl 0 bis 359): 0 zeigt nach oben (Richtung y = 0), im Uhrzeigersinn.
         * Gezeichnet als kleiner Keil am Rand des Spielers; fehlt das Feld, gibt es keinen.
         */
        facing: z.number().int().min(0).max(359).optional(),
        size: smallSize,
    }),
    z.object({ type: z.literal('cone'), x: coordinate, y: coordinate, size: smallSize }),
    /** Slalomstange: von oben ein kleiner Kreis. */
    z.object({ type: z.literal('pole'), x: coordinate, y: coordinate, size: smallSize }),
    /** Koordinationsring: flacher Ring, etwa so gross wie ein Spieler. */
    z.object({ type: z.literal('ring'), x: coordinate, y: coordinate, size: smallSize }),
    /** Huerde zum Ueberspringen; die Drehung (Grad) stellt sie quer zur Laufrichtung. */
    z.object({
        type: z.literal('hurdle'),
        x: coordinate,
        y: coordinate,
        rotation: z.union([z.literal(0), z.literal(45), z.literal(90), z.literal(135)]).optional(),
        size: smallSize,
    }),
    z.object({
        type: z.literal('goal'),
        x: coordinate,
        y: coordinate,
        size: z.enum(DIAGRAM_GOAL_SIZE),
        rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).optional(),
    }),
    z.object({ type: z.literal('ball'), x: coordinate, y: coordinate, size: smallSize }),
    z.object({
        type: z.literal('arrow'),
        kind: z.enum(DIAGRAM_ARROW_KIND),
        from: point,
        to: point,
        /**
         * Platz im Ablauf: gleiche Nummer = gleichzeitig. Ohne Nummer leitet der
         * Ablauf die Reihenfolge aus der Lage der Pfeile ab (alte Skizzen).
         */
        step: z.number().int().min(1).max(DIAGRAM_MAX_STEP).optional(),
        /**
         * "Laeuft bis Schritt": Laufweg, Dribbling und Positionswechsel gehen ueber mehrere Schritte. Der Spieler startet mit Schritt
         * `step` und kommt am Ende von Schritt `stepEnd` an - eine durchgehende Bewegung ohne Halt an den Schrittgrenzen. Nur mit `step`
         * (ohne Nummer leitet der Ablauf die Reihenfolge aus der Lage ab, da gibt es keine Schritt-Spanne) und nur groesser als `step`;
         * bei Pass, Schuss und hohem Ball nicht zulaessig. Fehlt das Feld, gehoert der Pfeil zu genau einem Schritt.
         */
        stepEnd: z.number().int().min(2).max(DIAGRAM_MAX_STEP).optional(),
        /**
         * Kontrollpunkt einer quadratischen Bezierkurve von `from` nach `to`: der Pfeil
         * ist dann gebogen (Bogenlauf, Flanke). Ohne `via` ist er gerade.
         */
        via: point.optional(),
        /**
         * Knickpunkte (1 bis 8 Zwischenpunkte): der Pfeil laeuft als Linienzug von `from` ueber alle Punkte nach `to`
         * (Slalom, Umweg). Ein Pfeil hat entweder `via` oder `points`, nie beides; ein hoher Ball (`LOFT`) hat keine.
         */
        points: z.array(point).min(1).max(DIAGRAM_ARROW_MAX_POINTS).optional(),
        /**
         * Nur mit `points`: der Weg ist eine glatte Kurve DURCH alle Punkte (Dribbling in Schlangenlinien, Laufbogen um
         * mehrere Stangen) statt eines Linienzugs mit abgerundeten Ecken. Ohne das Feld (oder `false`) bleibt es der Linienzug.
         */
        smooth: z.boolean().optional(),
        /**
         * Nur beim Pass ("Hinwechseln"): der Passgeber laeuft seinem Pass nach und wechselt auf die Position am Pfeilende
         * (passen und nachlaufen). Gezeichnet als duenne, gepunktete Begleitlinie neben dem Pass; im Ablauf laeuft der Spieler
         * am Pfeilanfang dem Ball hinterher. Fehlt das Feld, aendert sich nichts; `false` gibt es nicht - ausgeschaltet entfaellt das Feld.
         */
        follow: z.literal(true).optional(),
    }),
    /**
     * Text auf dem Feld (eine Zeile, 1 bis 40 Zeichen), mittig um `x`/`y`. Er steht fest - im Ablauf bewegt er sich
     * nie und gilt weder als Spieler noch als Ball. `size: 'S'` zeichnet ihn kleiner.
     */
    z.object({
        type: z.literal('text'),
        x: coordinate,
        y: coordinate,
        text: z
            .string()
            .trim()
            .min(1)
            .max(DIAGRAM_TEXT_MAX)
            .refine((value) => !/[\r\n]|\p{Zl}|\p{Zp}/u.test(value), { message: 'Der Text hat nur eine Zeile' }),
        size: smallSize,
    }),
    /**
     * Masslinie: technische Bemassung von `from` nach `to` mit Querstrichen an beiden Enden und
     * optionaler Beschriftung (z. B. „15 m“). Sie steht fest - im Ablauf bewegt sie sich nie und
     * gilt weder als Spieler noch als Ball.
     */
    z.object({
        type: z.literal('measure'),
        from: point,
        to: point,
        label: z.string().trim().min(1).max(DIAGRAM_MEASURE_LABEL_MAX).optional(),
    }),
    z.object({
        type: z.literal('zone'),
        x: coordinate,
        y: coordinate,
        w: coordinate,
        h: coordinate,
        /** Form im Rechteck x/y/w/h; ohne Angabe ein Rechteck. */
        shape: z.enum(DIAGRAM_ZONE_SHAPE).optional(),
        /** Farbe; ohne Angabe Weiss. */
        color: z.enum(DIAGRAM_ZONE_COLOR).optional(),
        /** Nur beim Dreieck: wohin die Spitze zeigt (0 oben, 90 rechts, 180 unten, 270 links). */
        rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).optional(),
    }),
]);
/**
 * Die Pfeilregeln stehen hier und nicht am Element: eine Pruefung am Objekt machte es zum Effekt,
 * und eine diskriminierte Union nimmt nur einfache Objekte. Auch Bausteine (Listen von Elementen) pruefen damit.
 */
export function checkDiagramArrows(diagram, ctx) {
    diagram.elements.forEach((element, index) => {
        if (element.type !== 'arrow')
            return;
        if (element.follow !== undefined && element.kind !== 'PASS') {
            ctx.addIssue({ code: 'custom', path: ['elements', index, 'follow'], message: 'Nur ein Pass kann „Hinwechseln“ (follow) haben' });
        }
        if (element.stepEnd !== undefined) {
            if (!DIAGRAM_MULTI_STEP_KINDS.includes(element.kind)) {
                ctx.addIssue({ code: 'custom', path: ['elements', index, 'stepEnd'], message: 'Nur Laufweg, Dribbling und Positionswechsel können über mehrere Schritte gehen (stepEnd)' });
            }
            else if (element.step === undefined) {
                ctx.addIssue({ code: 'custom', path: ['elements', index, 'stepEnd'], message: '„Läuft bis Schritt“ (stepEnd) braucht eine Schrittnummer (step)' });
            }
            else if (element.stepEnd <= element.step) {
                ctx.addIssue({ code: 'custom', path: ['elements', index, 'stepEnd'], message: '„Läuft bis Schritt“ (stepEnd) liegt nach dem Startschritt (step)' });
            }
        }
        if (element.smooth === true && element.points === undefined) {
            ctx.addIssue({ code: 'custom', path: ['elements', index, 'smooth'], message: 'Eine glatte Kurve (smooth) braucht Knickpunkte (points)' });
        }
        if (element.points === undefined)
            return;
        if (element.via !== undefined) {
            ctx.addIssue({ code: 'custom', path: ['elements', index, 'points'], message: 'Ein Pfeil hat entweder eine Kurve (via) oder Knickpunkte (points), nie beides' });
        }
        if (element.kind === 'LOFT') {
            ctx.addIssue({ code: 'custom', path: ['elements', index, 'points'], message: 'Ein hoher Ball lässt sich biegen, hat aber keine Knickpunkte' });
        }
    });
}
export const diagramSchema = z
    .object({
    pitch: z.enum(DIAGRAM_PITCH),
    /** Symbolgroesse der ganzen Skizze; ohne Angabe Normal (1). */
    symbolScale: z.union([z.literal(0.6), z.literal(0.8), z.literal(1)]).optional(),
    elements: z.array(diagramElementSchema).max(DIAGRAM_MAX_ELEMENTS, `Höchstens ${DIAGRAM_MAX_ELEMENTS} Elemente`),
})
    .superRefine(checkDiagramArrows);
/* ------------------------------------------------------------------ *
 * Uebungen
 * ------------------------------------------------------------------ */
export const TRAINING_LIMITS = {
    title: 150,
    focusTag: 40,
    focusTags: 10,
    material: 500,
    text: 4000,
    link: 500,
    planNotes: 4000,
    planFocus: 200,
    itemNote: 1000,
    items: 40,
    minutes: 240,
};
export const CATALOG_ID_PREFIX = 'catalog:';
/** Altersklasse als U-Zahl (U7 ... U21). */
const ageClass = z.number().int().min(4).max(21);
const playerCount = z.number().int().min(1).max(50);
const minutes = z.number().int().min(1).max(TRAINING_LIMITS.minutes);
/** Durchgaenge einer Uebung: Anzahl, Minuten je Durchgang und Pause in halben Schritten (siehe `training-load.ts`). */
const setCount = z
    .number()
    .int('Ganze Zahl von 1 bis 20')
    .min(EXERCISE_LOAD_LIMITS.sets.min, `Mindestens ${EXERCISE_LOAD_LIMITS.sets.min} Durchgang`)
    .max(EXERCISE_LOAD_LIMITS.sets.max, `Höchstens ${EXERCISE_LOAD_LIMITS.sets.max} Durchgänge`);
const halfMinutes = (limits, what) => z
    .number()
    .min(limits.min, `${what}: mindestens ${formatMinutes(limits.min)} Min.`)
    .max(limits.max, `${what}: höchstens ${formatMinutes(limits.max)} Min.`)
    .refine(isHalfStep, `${what}: nur ganze oder halbe Minuten`);
const setMinutes = halfMinutes(EXERCISE_LOAD_LIMITS.setMinutes, 'Minuten je Durchgang');
const pauseMinutes = halfMinutes(EXERCISE_LOAD_LIMITS.pauseMinutes, 'Pause');
/** Nur http und https - die Adresse wird in der Oberflaeche als Link angezeigt. */
const linkUrl = z
    .string()
    .trim()
    .max(TRAINING_LIMITS.link)
    .url('Ungültige Adresse')
    .refine((value) => /^https?:\/\//i.test(value), { message: 'Nur http- und https-Adressen sind erlaubt' });
/** Schlagworte werden kommagetrennt abgelegt - ein Komma im Wort ginge verloren. */
const focusTag = requiredText(TRAINING_LIMITS.focusTag).refine((value) => !value.includes(','), {
    message: 'Schlagworte dürfen kein Komma enthalten',
});
const focusTags = z
    .array(focusTag)
    .max(TRAINING_LIMITS.focusTags, `Höchstens ${TRAINING_LIMITS.focusTags} Schlagworte`);
/** Alter und Spielerzahl: von darf nicht groesser sein als bis. */
function checkRanges(value, ctx) {
    if (value.ageMin != null && value.ageMax != null && value.ageMin > value.ageMax) {
        ctx.addIssue({ code: 'custom', path: ['ageMax'], message: 'Die Altersklasse „bis“ liegt vor „von“' });
    }
    if (value.playersMin != null && value.playersMax != null && value.playersMin > value.playersMax) {
        ctx.addIssue({ code: 'custom', path: ['playersMax'], message: 'Die Spielerzahl „bis“ liegt unter „von“' });
    }
}
/**
 * Empfohlene Dauer: "bis" gehoert zu einem "von" und liegt nicht darunter; Durchgaenge gibt es ganz (Anzahl und
 * Minuten) oder gar nicht, eine Pause nur bei mehr als einem Durchgang, und ihre Gesamtdauer passt in eine Einheit.
 *
 * Beim Aendern (`partial`) steht `undefined` fuer "nicht mitgeschickt" und wird nicht geprueft; das Ergebnis
 * prueft der Server danach als Ganzes. Sonst zaehlt ein fehlender Wert wie `null`.
 */
export function checkLoad(value, ctx, partial = false) {
    const read = (field) => (partial ? field : (field ?? null));
    const from = read(value.durationMinutes);
    const to = read(value.durationMaxMinutes);
    const sets = read(value.sets);
    const perSet = read(value.setMinutes);
    const pause = read(value.pauseMinutes);
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (to != null && from === null)
        issue('durationMaxMinutes', 'Die Dauer „bis“ braucht auch ein „von“');
    else if (to != null && from != null && to < from)
        issue('durationMaxMinutes', 'Die Dauer „bis“ liegt unter „von“');
    if (sets != null && perSet === null)
        issue('setMinutes', 'Zu den Durchgängen gehören auch die Minuten je Durchgang');
    if (perSet != null && sets === null)
        issue('sets', 'Zu den Minuten je Durchgang gehört auch die Anzahl der Durchgänge');
    if (pause != null && pause > 0 && sets === null)
        issue('pauseMinutes', 'Eine Pause gehört zu Durchgängen');
    else if (pause != null && pause > 0 && sets === 1)
        issue('pauseMinutes', 'Bei einem Durchgang gibt es keine Pause');
    if (sets != null && perSet != null && setsTotalMinutes(sets, perSet, pause ?? 0) > TRAINING_LIMITS.minutes) {
        issue('sets', `Die Durchgänge dauern zusammen höchstens ${TRAINING_LIMITS.minutes} Minuten`);
    }
}
const exerciseFields = {
    title: requiredText(TRAINING_LIMITS.title),
    category: z.enum(EXERCISE_CATEGORY),
    focus: focusTags.default([]),
    ageMin: ageClass.nullish().transform((value) => value ?? null),
    ageMax: ageClass.nullish().transform((value) => value ?? null),
    playersMin: playerCount.nullish().transform((value) => value ?? null),
    playersMax: playerCount.nullish().transform((value) => value ?? null),
    /** Empfohlene Dauer: "von" bzw. Einzelwert, optional "bis" und Durchgaenge (siehe `checkLoad`). */
    durationMinutes: minutes.nullish().transform((value) => value ?? null),
    durationMaxMinutes: minutes.nullish().transform((value) => value ?? null),
    sets: setCount.nullish().transform((value) => value ?? null),
    setMinutes: setMinutes.nullish().transform((value) => value ?? null),
    pauseMinutes: pauseMinutes.nullish().transform((value) => value ?? null),
    material: trimmed(TRAINING_LIMITS.material).default(''),
    setup: trimmed(TRAINING_LIMITS.text).default(''),
    procedure: trimmed(TRAINING_LIMITS.text).default(''),
    coaching: trimmed(TRAINING_LIMITS.text).default(''),
    variations: trimmed(TRAINING_LIMITS.text).default(''),
    diagram: diagramSchema.nullish().transform((value) => value ?? null),
    /** Gesetzt = Linkuebung: es wird nur verlinkt, nichts kopiert. */
    linkUrl: linkUrl.nullish().transform((value) => value ?? null),
};
const exerciseObject = z.object(exerciseFields);
/** Eigene Uebung oder Linkuebung anlegen. */
export const exerciseInputSchema = exerciseObject.superRefine((value, ctx) => {
    checkRanges(value, ctx);
    checkLoad(value, ctx);
});
/** Aendern: nur die mitgeschickten Felder. Der Server prueft das Ergebnis erneut. */
export const exerciseUpdateSchema = exerciseObject.partial().superRefine((value, ctx) => {
    checkRanges(value, ctx);
    checkLoad(value, ctx, true);
});
/** Kopie einer Uebung: optional mit eigenem Titel. */
export const exerciseCopySchema = z.object({
    title: requiredText(TRAINING_LIMITS.title).optional(),
});
export const EXERCISE_SOURCE = ['CATALOG', 'OWN'];
export const EXERCISE_SOURCE_FILTER = ['ALL', ...EXERCISE_SOURCE];
const queryText = (max) => z.preprocess((value) => (value === '' ? undefined : value), z.string().trim().max(max).optional());
const queryInt = (min, max) => z.preprocess((value) => (value === '' ? undefined : value), z.coerce.number().int().min(min).max(max).optional());
export const exerciseListQuerySchema = z.object({
    /** Sucht in Titel, Schlagworten und Material. */
    search: queryText(100),
    category: z.preprocess((value) => (value === '' ? undefined : value), z.enum(EXERCISE_CATEGORY).optional()),
    /** Ein Schlagwort, genau (Gross-/Kleinschreibung egal). */
    focus: queryText(TRAINING_LIMITS.focusTag),
    /** Altersklasse als U-Zahl: passende Uebungen und solche ohne Altersangabe. */
    age: queryInt(4, 21),
    /** Spielerzahl: passende Uebungen und solche ohne Angabe. */
    players: queryInt(1, 50),
    source: z.preprocess((value) => (value === '' ? undefined : value), z.enum(EXERCISE_SOURCE_FILTER).default('ALL')),
});
/**
 * Eine Uebung des Standardkatalogs. Der Katalog liegt im Code; jede Uebung hat
 * einen stabilen Schluessel `<datei-kuerzel>-<slug>`, auf den Einheiten verweisen.
 */
export const catalogExerciseSchema = z
    .object({
    key: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)+$/, 'Schlüssel: Kleinbuchstaben, Ziffern und Bindestriche'),
    title: requiredText(TRAINING_LIMITS.title),
    category: z.enum(EXERCISE_CATEGORY),
    focus: focusTags.min(1),
    ageMin: ageClass.nullable(),
    ageMax: ageClass.nullable(),
    playersMin: playerCount.nullable(),
    playersMax: playerCount.nullable(),
    /** "von" der empfohlenen Dauer; `durationMaxMinutes` ("bis") hat jede Katalogubung, die Durchgaenge nur, wo der Text sie nennt. */
    durationMinutes: minutes,
    durationMaxMinutes: minutes,
    sets: setCount.optional(),
    setMinutes: setMinutes.optional(),
    pauseMinutes: pauseMinutes.optional(),
    material: trimmed(TRAINING_LIMITS.material),
    setup: requiredText(TRAINING_LIMITS.text),
    procedure: requiredText(TRAINING_LIMITS.text),
    coaching: requiredText(TRAINING_LIMITS.text),
    variations: trimmed(TRAINING_LIMITS.text),
    diagram: diagramSchema,
})
    .strict()
    .superRefine((value, ctx) => {
    checkRanges(value, ctx);
    checkLoad(value, ctx);
});
/* ------------------------------------------------------------------ *
 * Einheiten
 * ------------------------------------------------------------------ */
export const planItemInputSchema = z
    .object({
    phase: z.enum(PLAN_PHASE),
    /** Eigene Uebung des Vereins ... */
    exerciseId: z.string().min(1).nullish().transform((value) => value ?? null),
    /** ... oder Katalog-Uebung; beides leer = freier Eintrag. */
    catalogKey: z.string().min(1).nullish().transform((value) => value ?? null),
    /** Bei Uebungen als Kopie des Titels, damit die Einheit lesbar bleibt. */
    title: requiredText(TRAINING_LIMITS.title),
    durationMinutes: minutes,
    note: trimmed(TRAINING_LIMITS.itemNote).nullish().transform((value) => value || null),
})
    .superRefine((item, ctx) => {
    if (item.exerciseId && item.catalogKey) {
        ctx.addIssue({ code: 'custom', path: ['catalogKey'], message: 'Entweder eigene Übung oder Katalogübung' });
    }
});
const planFields = {
    focus: trimmed(TRAINING_LIMITS.planFocus).default(''),
    notes: trimmed(TRAINING_LIMITS.planNotes).default(''),
    /** Die ganze Liste in einem Rutsch - Reihenfolge = Reihenfolge der Eintraege. */
    items: z.array(planItemInputSchema).max(TRAINING_LIMITS.items, `Höchstens ${TRAINING_LIMITS.items} Einträge`).default([]),
};
/** Vorlage anlegen und Einheit (Kopf und Eintraege) ersetzen. */
export const trainingPlanInputSchema = z.object({
    title: requiredText(TRAINING_LIMITS.title),
    /** Nur Vorlagen: Mannschaft, fuer die die Vorlage gedacht ist. Leer = vereinsweit. */
    teamId: z.string().min(1).nullish().transform((value) => value ?? null),
    ...planFields,
});
/** Einheit eines Trainings: ohne Titel gilt der Titel des Trainings. */
export const sessionPlanInputSchema = z.object({
    title: trimmed(TRAINING_LIMITS.title).default(''),
    ...planFields,
});
/** Ersetzen einer Einheit ueber ihre ID: Titel bleibt Pflicht, Mannschaft aendert sich nicht. */
export const trainingPlanReplaceSchema = z.object({
    title: requiredText(TRAINING_LIMITS.title),
    ...planFields,
});
export const saveAsTemplateSchema = z.object({
    title: requiredText(TRAINING_LIMITS.title),
});
//# sourceMappingURL=training.js.map